import { useState, useEffect } from "react";
import useHttpUtility from "../utilities/useHttpUtility";
import useFormUtility from "../utilities/useFormUtility";
import useDateUtility from "../utilities/useDateUtility";
import LoadingScreen from "../components/LoadingScreen";
import MenuPage from "../components/MenuPage";
import MenuPageItemDiscard from "../components/MenuPageItemDiscard";
import MenuPageItemOk from "../components/MenuPageItemOk";
import FormFieldText from "../components/FormFieldText";
import FormFieldTime from "../components/FormFieldTime";
import FormFieldNumber from "../components/FormFieldNumber";
import FormFieldCheckbox from "../components/FormFieldCheckbox";
import FormFieldSelect from "../components/FormFieldSelect";

export default function WindowSection({ groupType, data, schedule, onDiscard, onOk }) {

    const dateUtility = useDateUtility();
    const httpUtility = useHttpUtility();

    const formUtility = useFormUtility(null);
    const [rates, setRates] = useState(null);

    useEffect(() => {
        httpUtility.get("/api/Rates", (d) => {
            setRates(d);
            formUtility.setData(
                data
                ? {
                    ...data,
                    dayOfWeek: data.dayOfWeek.toString(),
                    startTime: dateUtility.toTimeSpanString(data.startTime),
                    endTime: dateUtility.toTimeSpanString(data.endTime),
                    rateId: data.scheduleId && !data.rateId
                        ? "0"
                        : data.rateId?.toString()
                }
                : {
                    id: null,
                    name: schedule?.name ?? "",
                    description: schedule?.description ?? "",
                    scheduleId: schedule?.id ?? null,
                    depot: schedule?.depot ?? null,
                    service: schedule?.speed ?? null,
                    dayOfWeek: schedule?.dayOfWeek?.toString() ?? "1",
                    startTime: dateUtility.toTimeSpanString(schedule?.startTime ?? "08:00:00"),
                    endTime: dateUtility.toTimeSpanString(schedule?.endTime ?? "17:00:00"),
                    preparation: schedule?.preparation ?? 2,
                    preparationMin: schedule?.preparation ?? 0,
                    rateId: groupType === 2? null
                        : schedule?.id ? "0"
                        : d.length > 0 ? d[0].id.toString()
                        : null,
                    showDateTime: true,
                    phoneRequired: true,
                    isWidgetCalendar: false
                },
                !data)
        });
    }, []);

    function handleOk() {
        if (!formUtility.data.name || formUtility.data.name.trim().length < 1)
            return formUtility.showNotification(2, "Name is required.");

        let preparation = parseInt(formUtility.data.preparation);

        if (isNaN(preparation) || formUtility.data.preparation.toString().trim().length < 1)
            return formUtility.showNotification(2, "Preparation is required.");
            
        if (preparation < formUtility.data.preparationMin)
            return formUtility.showNotification(2, `Minimum preparation time is ${formUtility.data.preparationMin}.`);

        if (!formUtility.data.startTime)
            return formUtility.showNotification(2, "Start time is required.");

        if (!formUtility.data.endTime)
            return formUtility.showNotification(2, "End time is required.");

        if (formUtility.data.startTime > formUtility.data.endTime)
            return formUtility.showNotification(2, "Invalid start and end time.");

        if ((groupType === 1 && formUtility.data.scheduleId && !formUtility.data.rateId)
            || (groupType === 1 && !formUtility.data.scheduleId && !formUtility.data.isWidgetCalendar && (!formUtility.data.rateId || formUtility.data.rateId === "0"))
            || (groupType === 3 && (!formUtility.data.rateId || formUtility.data.rateId === "0")))
            return formUtility.showNotification(2, "Rate is required.");

        if ((!formUtility.data.rateId || formUtility.data.rateId === "0")
            && (groupType === 3
                || (groupType === 1 && !formUtility.data.scheduleId && !formUtility.data.isWidgetCalendar)))
            return formUtility.showNotification(2, "Rate is required.");

        var result = {
            ...formUtility.data,
            name: formUtility.data.name.trim(),
            description: formUtility.data.description?.trim(),
            dayOfWeek: parseInt(formUtility.data.dayOfWeek),
            startTime: dateUtility.toTimeSpanString(formUtility.data.startTime),
            endTime: dateUtility.toTimeSpanString(formUtility.data.endTime),
            preparation: preparation,
            rateId: groupType === 2 || !formUtility.data.rateId || formUtility.data.rateId === "0" || (!formUtility.data.scheduleId && formUtility.data.isWidgetCalendar)
                ? null
                : parseInt(formUtility.data.rateId),
            isWidgetCalendar: !formUtility.data.scheduleId && formUtility.data.isWidgetCalendar
        };
        
        onOk(result);
    }

    return (
        !formUtility.data
            ? <LoadingScreen isFullScreen={false} />
            :
            <div className="window-section">
                <MenuPage>
                    <MenuPageItemDiscard formUtility={formUtility} onClick={onDiscard} />
                    <MenuPageItemOk formUtility={formUtility} onClick={handleOk} />
                </MenuPage>
                <div>
                    <FormFieldText formUtility={formUtility} field="name" label="Name" max="30" />
                    <FormFieldText formUtility={formUtility} field="description" label="Description (Optional)" max="250" />
                    {
                        formUtility.data.scheduleId
                            ?
                            <>
                                <FormFieldText formUtility={formUtility} field="depot" label="Depot" disabled={true} />
                                <FormFieldText formUtility={formUtility} field="service" label="Service" disabled={true} />
                            </>
                            : null
                    }
                    <FormFieldSelect
                        formUtility={formUtility}
                        disabled={formUtility.data.scheduleId}
                        field="dayOfWeek"
                        label="Day of Week"
                        options={dateUtility.daysOfWeek} />
                    <FormFieldTime formUtility={formUtility} field="startTime" label="Start Time" disabled={formUtility.data.scheduleId} />
                    <FormFieldTime formUtility={formUtility} field="endTime" label="End Time" disabled={formUtility.data.scheduleId} />
                    <FormFieldNumber
                        formUtility={formUtility}
                        integerOnly={true}
                        field="preparation"
                        label="Preparation (Hours)"
                        description={groupType !== 3 ? "Preparation time is calculated from <b>start</b> time." : "Preparation time is calculated from <b>end</b> time."}
                    >
                        {groupType !== 2 ? <p>Warning: Preparation time is calculated at the time Shopify request rates, Shopify may also cache rates for a short period.  It is recommended to add extra preparation time to deal with this and also allow the user sufficient time to complete the order after getting rates.</p> : null}
                    </FormFieldNumber>
                    {
                        groupType !== 2 && !formUtility.data.isWidgetCalendar
                            ?
                            <FormFieldSelect
                                formUtility={formUtility}
                                field="rateId"
                                label="Rate"
                                options={
                                    formUtility.data.scheduleId
                                        ? [{ name: "( - AUTO - )", value: 0 }, ...rates.map(r => ({ name: r.name, value: r.id }))]
                                        : rates.map(r => ({ name: r.name, value: r.id }))
                                } />
                            : null
                    }
                    {
                        groupType !== 2
                            ?
                            <>
                                <FormFieldCheckbox formUtility={formUtility} field="showDateTime" label="Show Date Time" description="When checked the checkout rate will show a date and time of the window in the name." />
                                <FormFieldCheckbox formUtility={formUtility} field="phoneRequired" label="Phone Required" description="When checked the checkout rate will require the user to input a phone number. checked the checkout rate will show a date and time of the window in the name." />
                            </>
                            : null
                    }
                    {
                        groupType === 1 && !formUtility.data.scheduleId
                            ? <FormFieldCheckbox formUtility={formUtility} field="isWidgetCalendar" label="Is Widget Calendar" description="For use with the calendar widget. When checked, this window will not show a rate at checkout, it will be used as an additional calendar entry for the widget for Shopify, third party and standard rates." />
                            : null
                    }
                </div>
            </div>
    );
}